<template>
  <div class="match-wave">
    <div
      v-for="(mark, idx) in regionMarks"
      :key="`mark_${idx}`"
      class="match-wave__labels"
    >
      <span
        class="match-wave__label"
        :style="{ left: mark.startPct + '%' }"
      >{{ mark.startText }}</span>
      <span
        class="match-wave__label"
        :style="{ left: mark.endPct + '%' }"
      >{{ mark.endText }}</span>
    </div>
    <div ref="wave" class="match-wave__el" />
    <audio ref="audio" class="match-wave__audio" preload="metadata" />
  </div>
</template>

<script>
import axios from 'axios'
import WaveSurfer from 'wavesurfer.js'
import RegionsPlugin from 'wavesurfer.js/dist/plugin/wavesurfer.regions.min.js'
import { getWavedata } from '@/api/common.js'

const REGION_COLOR = 'rgba(229, 42, 13, 0.22)'
const HANDLE_STYLE = {
  left: {
    width: '1px',
    maxWidth: '1px',
    backgroundColor: '#000'
  },
  right: {
    width: '1px',
    maxWidth: '1px',
    backgroundColor: '#000'
  }
}

export default {
  name: 'MatchWave',
  props: {
    music: {
      type: Object,
      required: true
    },
    matches: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      wavesurfer: null,
      peaks: [],
      localPlaying: false,
      regionMarks: [],
      seekingPlay: false
    }
  },
  computed: {
    musicId() {
      return this.music.music_id || this.music.song_id || this.music.object_id || ''
    },
    audioSrc() {
      return this.music.watermark_file || this.music.watermark_url || ''
    }
  },
  watch: {
    musicId: {
      handler(id) {
        if (id) this.$nextTick(() => this.initWave())
      }
    },
    matches: {
      deep: true,
      handler() {
        this.renderRegions()
      }
    }
  },
  mounted() {
    if (this.musicId) this.initWave()
  },
  beforeDestroy() {
    this.destroyWave()
  },
  methods: {
    formatClock(sec) {
      const s = Math.max(0, Math.floor(sec || 0))
      const m = Math.floor(s / 60)
      const r = s % 60
      return `${m}:${String(r).padStart(2, '0')}`
    },
    destroyWave() {
      if (this.wavesurfer) {
        this.wavesurfer.destroy()
        this.wavesurfer = null
      }
      this.localPlaying = false
      this.regionMarks = []
    },
    async fetchPeaks() {
      if (!this.musicId) {
        this.peaks = []
        return
      }
      try {
        const data = await getWavedata({ music_ids: this.musicId })
        const waveurl = data && data.data && data.data.waveformhera
        if (data && data.code == 0 && waveurl) {
          const res = await axios.get(waveurl)
          this.peaks = Array.isArray(res.data) ? res.data : []
        } else {
          this.peaks = []
        }
      } catch (err) {
        console.error(err)
        this.peaks = []
      }
    },
    async initWave() {
      if (!this.$refs.wave || !this.audioSrc) return
      this.destroyWave()
      await this.fetchPeaks()
      if (!this.$refs.wave || !this.$refs.audio) return

      const audio = this.$refs.audio
      this.wavesurfer = WaveSurfer.create({
        container: this.$refs.wave,
        backend: 'MediaElement',
        media: audio,
        waveColor: '#d0d0d0',
        progressColor: '#e52a0d',
        cursorColor: 'transparent',
        cursorWidth: 0,
        barWidth: 1,
        barGap: 2,
        height: 36,
        normalize: false,
        barMinHeight: 0.1,
        interact: true,
        plugins: [
          RegionsPlugin.create({
            dragSelection: false
          })
        ]
      })

      this.wavesurfer.on('ready', () => {
        this.renderRegions()
      })
      this.wavesurfer.on('seek', () => {
        this.onWaveSeek()
      })
      this.wavesurfer.on('play', () => {
        this.localPlaying = true
        this.$emit('play-state', true)
      })
      this.wavesurfer.on('pause', () => {
        this.localPlaying = false
        this.$emit('play-state', false)
      })
      this.wavesurfer.on('finish', () => {
        this.localPlaying = false
        this.$emit('play-state', false)
      })

      const peaks = this.peaks.length > 0 ? this.peaks : undefined
      this.wavesurfer.load(this.audioSrc, peaks)
    },
    onWaveSeek() {
      if (!this.wavesurfer || this.seekingPlay) return
      this.$emit('play-start')
      if (this.wavesurfer.isPlaying()) return
      this.seekingPlay = true
      this.$nextTick(() => {
        if (this.wavesurfer && !this.wavesurfer.isPlaying()) {
          this.wavesurfer.play()
        }
        setTimeout(() => {
          this.seekingPlay = false
        }, 80)
      })
    },
    renderRegions() {
      if (!this.wavesurfer) return
      this.wavesurfer.clearRegions()
      const duration =
        this.wavesurfer.getDuration() ||
        Number(this.music.duration_sec) ||
        Number(this.music.duration) ||
        0
      const list = Array.isArray(this.matches) ? this.matches : []
      const marks = []
      list.forEach((m) => {
        let start = Number(m.lib_start_sec)
        let end = Number(m.lib_end_sec)
        if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return
        if (duration > 0) {
          start = Math.max(0, Math.min(start, duration))
          end = Math.max(start, Math.min(end, duration))
        }
        if (end <= start) return
        this.wavesurfer.addRegion({
          start,
          end,
          color: REGION_COLOR,
          drag: false,
          resize: false,
          handleStyle: HANDLE_STYLE
        })
        if (duration > 0) {
          marks.push({
            startPct: (start / duration) * 100,
            endPct: (end / duration) * 100,
            startText: this.formatClock(start),
            endText: this.formatClock(end)
          })
        }
      })
      this.regionMarks = marks
    },
    isPlaying() {
      return !!(this.wavesurfer && this.wavesurfer.isPlaying())
    },
    play(start) {
      if (!this.wavesurfer) return
      if (typeof start === 'number' && Number.isFinite(start)) {
        this.wavesurfer.play(start)
      } else {
        this.wavesurfer.play()
      }
    },
    pause() {
      if (!this.wavesurfer) return
      this.wavesurfer.pause()
    },
    togglePlay() {
      if (!this.wavesurfer) return
      this.wavesurfer.playPause()
    }
  }
}
</script>

<style lang="scss" scoped>
.match-wave {
  position: relative;
  width: 100%;
  padding-top: 14px;
  box-sizing: border-box;

  &__labels {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 14px;
    pointer-events: none;
    z-index: 2;
  }

  &__label {
    position: absolute;
    top: 0;
    transform: translateX(-50%);
    font-size: 10px;
    color: #333;
    line-height: 1;
    white-space: nowrap;
    pointer-events: none;
  }

  &__el {
    width: 100%;
    height: 36px;
  }

  &__audio {
    display: none;
  }

  ::v-deep .wavesurfer-region {
    pointer-events: none;
    z-index: 1;
  }

  ::v-deep .wavesurfer-handle {
    pointer-events: none;
  }

  ::v-deep .wavesurfer-handle-start,
  ::v-deep .wavesurfer-handle-end {
    width: 1px !important;
    max-width: 1px !important;
    background-color: #000 !important;
  }
}
</style>
